import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Onboarding } from 'services/user'
import { AdAccountLinks } from 'services/user/ad-account-links'
import { BingAuthConfig, FacebookAuthConfig, GoogleAuthConfig } from 'config'
import { getOAuthCodeKey } from 'utils/oauth'
import { isZeroSpendError, parseError } from 'utils/errors'
import { ZeroSpendModal } from 'components/zero-spend-modal/zero-spend-modal'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import { useStores } from 'hooks'
import {
	Platform,
	InternalStep,
	ConnectAdAccountComponentProps,
	AccountSelectionState,
	AccountLinkType,
	GoogleOAuthAccountLinkDTO,
	FacebookAccountLinkDTO,
	BingAccountLinkDTO,
	BingAdsAccountBasicInfoDTO,
	MCCAccountLinkDTO
} from './types'
import { HeaderSection } from './header-section'
import { PlatformSelectionView } from './platform-selection-view'
import { WaitingView } from './waiting-view'
import { AccountSelectionView } from './account-selection-view'
import { DoneView } from './done-view'
import { TrackingConfirmationView } from './tracking-confirmation-view'
import { SpendScreeningModal, ScreeningTier } from './spend-screening-view'

export const ConnectAdAccountComponent = ({
	account,
	asset,
	onSuccess,
	isOnboarding = true,
	screenSpend = false,
	onAdAccountConnected
}: ConnectAdAccountComponentProps) => {
	const trackPostHogEvent = useTrackPostHogEvent()
	const navigate = useNavigate()
	const { assetStore } = useStores()
	const [platform, setPlatform] = useState<Platform | null>(null)
	const [internalStep, setInternalStep] = useState<InternalStep>('idle')
	const [loading, setLoading] = useState(false)
	const [prefetching, setPrefetching] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [showZeroSpendModal, setShowZeroSpendModal] = useState(false)
	const [screeningTier, setScreeningTier] = useState<ScreeningTier | null>(null)
	const [accountLink, setAccountLink] = useState<AccountLinkType | null>(null)
	const mccSelectionRequest = useRef(0)
	const [mccLink, setMccLink] = useState<MCCAccountLinkDTO | null>(null)
	const [mccClientLoading, setMccClientLoading] = useState(false)
	const [accountSelection, setAccountSelection] =
		useState<AccountSelectionState>({
			selected: null,
			client: null,
			tracking: true,
			set_custom_audience: true
		})
	const [availableAccountLinks, setAvailableAccountLinks] = useState<
		AccountLinkType[]
	>([])
	const [selectedAccountLinkId, setSelectedAccountLinkId] = useState<
		string | null
	>(null)

	const [selectedAccountInfo, setSelectedAccountInfo] = useState<{
		name?: string
		account_number?: string
	} | null>(null)
	const [pixelOptions, setPixelOptions] = useState<
		Array<{ id: string; name: string }>
	>([])
	const [loadingPixels, setLoadingPixels] = useState(false)
	// Pre-fetched links organized by platform for instant access
	const [prefetchedLinksByPlatform, setPrefetchedLinksByPlatform] = useState<
		Record<Platform, AccountLinkType[]>
	>({
		GADS: [],
		FADS: [],
		BADS: []
	})

	// Pre-fetch all account links for all platforms in the background
	useEffect(() => {
		const preFetchAllPlatformLinks = async () => {
			if (!account?.id) {
				setPrefetching(false)
				return
			}

			setPrefetching(true)

			try {
				// Load basic links
				const links = await AdAccountLinks.find(account.id)

				// Pre-fetch full details for all active links in the background
				const activeLinks = links.filter((link) => link.status === 'OK')
				const platforms: Platform[] = ['GADS', 'FADS', 'BADS']

				// Load full details for each platform's links in parallel
				const platformLinksPromises = platforms.map(async (platform) => {
					const platformLinks = activeLinks.filter(
						(link) => link.platform === platform
					)

					if (platformLinks.length === 0) return []

					const linksWithDetails = await Promise.all(
						platformLinks.map(async (link) => {
							try {
								const fullLink = await AdAccountLinks.getById(
									account.id,
									link.id
								)
								return fullLink as AccountLinkType
							} catch (err) {
								console.error(`Failed to pre-fetch link ${link.id}:`, err)
								return null
							}
						})
					)

					return linksWithDetails.filter(
						(link): link is AccountLinkType => link !== null
					)
				})

				const [gadsLinks, fadsLinks, badsLinks] = await Promise.all(
					platformLinksPromises
				)

				setPrefetchedLinksByPlatform({
					GADS: gadsLinks,
					FADS: fadsLinks,
					BADS: badsLinks
				})
			} catch (err) {
				console.error('Failed to pre-fetch platform links:', err)
			} finally {
				setPrefetching(false)
			}
		}

		preFetchAllPlatformLinks()
	}, [account?.id])

	// Use pre-fetched links when entering account selection
	useEffect(() => {
		if (!platform || internalStep !== 'account-selection') return

		// Use pre-fetched links for instant access
		const validLinks = prefetchedLinksByPlatform[platform] || []

		setAvailableAccountLinks(validLinks)

		if (accountLink && validLinks.length > 0) {
			const currentLinkIndex = validLinks.findIndex(
				(link) => link.id === accountLink.id
			)
			if (currentLinkIndex >= 0) {
				setSelectedAccountLinkId(accountLink.id)
			} else {
				setSelectedAccountLinkId(validLinks[0].id)
				setAccountLink(validLinks[0])
			}
		} else if (validLinks.length > 0) {
			setSelectedAccountLinkId(validLinks[0].id)
			setAccountLink(validLinks[0])
		}
	}, [platform, internalStep, prefetchedLinksByPlatform])

	// Watch for OAuth code in localStorage
	useEffect(() => {
		if (internalStep === 'waiting' && platform) {
			const interval = setInterval(() => {
				const codeKey = getOAuthCodeKey(platform)
				const code = window.localStorage.getItem(codeKey)
				if (code) {
					clearInterval(interval)
					window.localStorage.removeItem(codeKey)
					handleOAuthCode(code)
				}
			}, 1000)

			return () => clearInterval(interval)
		}
	}, [internalStep, platform])

	const handleOAuthCode = async (code: string) => {
		if (!platform || !account?.id) return

		setLoading(true)
		setError(null)

		try {
			const response = await Onboarding.setAccountLinkForAsset(
				account.id,
				asset.id,
				code,
				platform
			)

			const link = (response as any).link
			if (link) {
				setAccountLink(link)
				setSelectedAccountLinkId(link.id)
				setInternalStep('account-selection')

				try {
					// Update pre-fetched links for this platform
					setPrefetchedLinksByPlatform((prev) => ({
						...prev,
						[platform]: prev[platform].find((l) => l.id === link.id)
							? prev[platform]
							: [...prev[platform], link]
					}))

					// Update available links
					setAvailableAccountLinks((prev) => {
						if (prev.find((l) => l.id === link.id)) {
							return prev
						}
						return [...prev, link]
					})
				} catch (err) {
					console.error('Failed to refresh links:', err)
				}
			} else {
				setError('Failed to retrieve account information')
				setInternalStep('idle')
			}
		} catch (err) {
			setError(parseError(err) || 'Failed to connect account')
			setInternalStep('idle')
		} finally {
			setLoading(false)
		}
	}

	const handlePlatformSelect = (selectedPlatform: Platform) => {
		setPlatform(selectedPlatform)
		setError(null)

		// Use pre-fetched links for instant access
		const preFetchedLinks = prefetchedLinksByPlatform[selectedPlatform] || []

		if (preFetchedLinks.length > 0) {
			// User has existing authorization - use pre-fetched data instantly
			setAccountLink(preFetchedLinks[0])
			setSelectedAccountLinkId(preFetchedLinks[0].id)
			setInternalStep('account-selection')
			return
		}

		// No existing authorization - proceed with OAuth flow
		setInternalStep('waiting')
		setLoading(true)

		let authUrl = ''
		if (selectedPlatform === 'GADS') {
			authUrl = GoogleAuthConfig.AUTH_URL
		} else if (selectedPlatform === 'FADS') {
			authUrl = FacebookAuthConfig.AUTH_URL
		} else if (selectedPlatform === 'BADS') {
			authUrl = BingAuthConfig.AUTH_URL
		}

		if (authUrl) {
			window.open(authUrl, '_blank')
		}
	}

	const handleCancelAuth = () => {
		setInternalStep('idle')
		setPlatform(null)
		setLoading(false)
	}

	const handleCreateNewAuthorization = () => {
		if (!platform || !account?.id) return

		// Track PostHog event for trying new auth
		const platformMap: Record<Platform, string> = {
			GADS: 'google_ads',
			FADS: 'meta_ads',
			BADS: 'microsoft_ads'
		}
		trackPostHogEvent(
			PostHogEvent.ONBOARDING_TRY_NEW_AUTH,
			PostHogPath.ONBOARDING,
			{
				ad_platform: platformMap[platform] || platform.toLowerCase()
			}
		)

		setAccountLink(null)
		setMccLink(null)
		setAccountSelection({
			selected: null,
			client: null,
			tracking: true,
			set_custom_audience: true
		})
		setError(null)
		setInternalStep('waiting')
		setLoading(true)

		let authUrl = ''
		if (platform === 'GADS') {
			authUrl = GoogleAuthConfig.AUTH_URL
		} else if (platform === 'FADS') {
			authUrl = FacebookAuthConfig.AUTH_URL
		} else if (platform === 'BADS') {
			authUrl = BingAuthConfig.AUTH_URL
		}

		if (authUrl) {
			window.open(authUrl, '_blank')
		}
	}

	// Fetch tracking info and transition to confirmation step (Google Ads)
	const handleFetchTrackingInfo = async (
		accountNumber: string,
		linkId: string,
		fetchPlatform?: string,
		options?: { customer_id?: string; ad_account_id?: string }
	) => {
		if (!account?.id || !asset?.id) return

		setLoading(true)
		setError(null)

		try {
			const trackingInfo = await Onboarding.getTrackingInfo(
				account.id,
				asset.id,
				linkId,
				accountNumber,
				fetchPlatform || 'GADS',
				options
			)

			// Store selected account info for the banner on the confirmation screen
			const selectedAccount = (accountSelection.client ||
				accountSelection.selected) as any
			setSelectedAccountInfo({
				name: selectedAccount?.name || selectedAccount?.descriptive_name || '',
				account_number: accountNumber
			})

			setAccountSelection((prev) => ({
				...prev,
				existingTT: trackingInfo.is_clickguard_tt
					? null
					: trackingInfo.tracking_template,
				existingUrlSuffix: trackingInfo.final_url_suffix,
				campaignsToUpdate: trackingInfo.campaigns_to_update || []
			}))

			setInternalStep('tracking-confirmation')
		} catch (err) {
			setError(parseError(err) || 'Failed to fetch tracking information')
		} finally {
			setLoading(false)
		}
	}

	// Handle tracking confirmation — create the ad account with confirmed preferences
	const handleTrackingConfirm = async (params: {
		useClickGuardTT: boolean
		customUrlSuffix?: string
	}) => {
		if (!account?.id || !asset?.id || !platform || !accountLink) return

		setLoading(true)
		setError(null)

		try {
			const accountNumber =
				mccLink && accountSelection.client
					? accountSelection.client.account_number
					: (accountSelection.selected as any)?.account_number

			const linkId = mccLink ? mccLink.id : accountLink.id

			const payload: any = {
				account_number: accountNumber,
				link_id: linkId,
				set_tracking_template: params.useClickGuardTT,
				custom_url_suffix: params.customUrlSuffix
			}

			// Add Bing-specific fields
			if (platform === 'BADS' && accountSelection.selected) {
				const selectedAccount =
					accountSelection.selected as BingAdsAccountBasicInfoDTO
				const bingLink = accountLink as BingAccountLinkDTO
				payload.customer_id = bingLink.external_id || ''
				payload.ad_account_id = selectedAccount.id?.toString() || ''
			}

			const result = await Onboarding.addAdAccountToAsset(
				account.id,
				asset.id,
				payload
			)

			if (result.type === 'account' && result.data) {
				const selectedAccount = (accountSelection.client ||
					accountSelection.selected) as any
				setSelectedAccountInfo({
					name: selectedAccount?.name || result.data?.name,
					account_number:
						selectedAccount?.account_number ||
						result.data?.details?.account_number ||
						result.data?.account_number
				})

				await assetStore.refreshAdAccounts()
				const adAccountsCount = assetStore.adAccounts.length
				trackPostHogEvent(
					PostHogEvent.ONBOARDING_COMPLETE_AD_ACCOUNT_STEP,
					PostHogPath.ONBOARDING,
					{
						ad_accounts_connected: adAccountsCount,
						automatic_toggle: params.useClickGuardTT ? 'on' : 'off'
					}
				)

				onAdAccountConnected?.()
				setInternalStep('done')
				onSuccess?.()
			} else {
				setInternalStep('done')
				onSuccess?.()
			}
		} catch (err) {
			if (
				(err as any)?.response?.data?.code === 'AD_ACCOUNT_SPEND_LOW' ||
				(err as any)?.response?.data?.code === 'AD_ACCOUNT_FIT_CALL_REQUIRED'
			) {
				setScreeningTier(
					(err as any).response.data.code === 'AD_ACCOUNT_SPEND_LOW'
						? 'low'
						: 'screening'
				)
			} else if (isZeroSpendError(err)) {
				showNoSpend()
			} else {
				setError(parseError(err) || 'Failed to add account')
			}
		} finally {
			setLoading(false)
		}
	}

	// An account with no spend. In onboarding it is the "not enough activity"
	// case of the screening modal; everywhere else, the zero-spend modal.
	const showNoSpend = () => {
		if (screenSpend) setScreeningTier('low')
		else setShowZeroSpendModal(true)
	}

	/**
	 * Qualification and screening (onboarding only). Before anything is
	 * connected, ask where the picked account's last-30-day spend puts it:
	 * zero keeps today's "no recent ad spend" modal, low asks for another
	 * account, screening offers a call or a clean exit, qualified goes on.
	 * Returns whether the normal flow may continue. A failed check never
	 * blocks: the step behaves as it did before.
	 */
	useEffect(() => {
		const selected = accountSelection.client || accountSelection.selected
		const number = (selected as any)?.account_number
		const linkId = mccLink?.id || accountLink?.id
		if (
			(mccLink && !accountSelection.client) ||
			!number ||
			!linkId ||
			!account?.id ||
			!asset?.id ||
			!platform ||
			(selected as any)?.manager ||
			account.has_connected_ad_account
		)
			return
		void Onboarding.checkAdAccountSpend(
			account.id,
			asset.id,
			linkId,
			number,
			platform,
			platform === 'BADS'
				? {
						customer_id: (accountLink as BingAccountLinkDTO)?.external_id || '',
						ad_account_id: String(
							(selected as BingAdsAccountBasicInfoDTO)?.id || ''
						)
				  }
				: undefined
		).catch(() => undefined)
	}, [
		accountSelection.client,
		accountSelection.selected,
		mccLink?.id,
		accountLink?.id,
		account?.id,
		asset?.id,
		platform
	])

	const passesSpendScreening = async (
		accountNumber: string,
		linkId: string
	): Promise<boolean> => {
		if (!account?.id || !asset?.id || !platform) return true
		setLoading(true)
		setError(null)
		try {
			const { tier } = await Onboarding.checkAdAccountSpend(
				account.id,
				asset.id,
				linkId,
				accountNumber,
				platform,
				platform === 'BADS'
					? {
							customer_id:
								(accountLink as BingAccountLinkDTO)?.external_id || '',
							ad_account_id: String(
								(accountSelection.selected as BingAdsAccountBasicInfoDTO)?.id ||
									''
							)
					  }
					: undefined
			)
			// A big account goes on like any other; its VIP setup offer is the
			// interstitial after the account connects (setup-v2).
			if (tier === 'qualified' || tier === 'vip') return true
			const picked = (accountSelection.client ||
				accountSelection.selected) as any
			setSelectedAccountInfo({
				name: picked?.name || picked?.descriptive_name || '',
				account_number: accountNumber
			})
			// No spend at all is the same case as too little: one modal.
			setScreeningTier(tier === 'zero' ? 'low' : tier)
			return false
		} catch {
			return true
		} finally {
			setLoading(false)
		}
	}

	const handleAccountSubmit = async () => {
		if (!account?.id || !asset?.id || !platform || !accountLink) return

		const pickedNumber =
			mccLink && accountSelection.client
				? accountSelection.client.account_number
				: (accountSelection.selected as any)?.account_number
		if (pickedNumber) {
			const linkId =
				mccLink && accountSelection.client ? mccLink.id : accountLink.id
			if (!(await passesSpendScreening(pickedNumber, linkId))) return
		}

		if (mccLink && accountSelection.client) {
			// For Google Ads MCC flow: intercept and show tracking confirmation
			if (platform === 'GADS') {
				await handleFetchTrackingInfo(
					accountSelection.client.account_number,
					mccLink.id
				)
				return
			}

			setLoading(true)
			setError(null)

			try {
				const result = await Onboarding.addAdAccountToAsset(
					account.id,
					asset.id,
					{
						account_number: accountSelection.client.account_number,
						link_id: mccLink.id,
						set_tracking_template: accountSelection.tracking,
						set_custom_audience:
							platform === 'FADS'
								? accountSelection.set_custom_audience
								: undefined,
						pixel_id:
							platform === 'FADS' ? accountSelection.pixel_id : undefined
					}
				)

				if (result.type === 'account' && result.data) {
					// Store selected account info for display
					const selectedClient = accountSelection.client as any
					setSelectedAccountInfo({
						name: selectedClient?.name || result.data?.name,
						account_number:
							selectedClient?.account_number ||
							result.data?.details?.account_number ||
							result.data?.account_number
					})

					// Track PostHog event for completing ad account step
					// Refresh ad accounts to get accurate count
					await assetStore.refreshAdAccounts()
					const adAccountsCount = assetStore.adAccounts.length
					trackPostHogEvent(
						PostHogEvent.ONBOARDING_COMPLETE_AD_ACCOUNT_STEP,
						PostHogPath.ONBOARDING,
						{
							ad_accounts_connected: adAccountsCount,
							automatic_toggle: accountSelection.tracking ? 'on' : 'off'
						}
					)

					// Signal that ad account is connected (for sidebar step completion)
					onAdAccountConnected?.()

					setInternalStep('done')
					onSuccess?.()
				} else {
					setInternalStep('done')
					onSuccess?.()
				}
			} catch (err) {
				setError(parseError(err) || 'Failed to add account')
			} finally {
				setLoading(false)
			}
			return
		}

		if (!accountSelection.selected) return

		// For Google Ads direct (non-MCC) flow: intercept and show tracking confirmation
		if (platform === 'GADS') {
			const accountNumber = (accountSelection.selected as any).account_number
			await handleFetchTrackingInfo(accountNumber, accountLink.id)
			return
		}

		// For Bing Ads: intercept and show tracking confirmation
		if (platform === 'BADS') {
			const selectedAccount =
				accountSelection.selected as BingAdsAccountBasicInfoDTO
			const bingLink = accountLink as BingAccountLinkDTO
			const accountNumber = selectedAccount.account_number
			await handleFetchTrackingInfo(accountNumber, accountLink.id, 'BADS', {
				customer_id: bingLink.external_id || '',
				ad_account_id: selectedAccount.id?.toString() || ''
			})
			return
		}

		setLoading(true)
		setError(null)

		try {
			const accountNumber =
				(accountSelection.selected as any).account_number ||
				(accountSelection.selected as any).account_number

			// Only FADS reaches this code path (GADS + BADS are intercepted above)
			const payload: any = {
				account_number: accountNumber,
				link_id: accountLink.id,
				set_custom_audience: accountSelection.set_custom_audience,
				pixel_id: accountSelection.pixel_id
			}

			const result = await Onboarding.addAdAccountToAsset(
				account.id,
				asset.id,
				payload
			)

			if (result.type === 'link' && result.data) {
				setMccLink(result.data as MCCAccountLinkDTO)
				setAccountSelection({
					...accountSelection,
					selected: result.data as any
				})
			} else if (result.type === 'account' && result.data) {
				// Store selected account info for display
				const selectedAccount = accountSelection.selected as any
				setSelectedAccountInfo({
					name: selectedAccount?.name || result.data?.name,
					account_number:
						selectedAccount?.account_number ||
						result.data?.details?.account_number ||
						result.data?.account_number
				})

				// Track PostHog event for completing ad account step
				// Refresh ad accounts to get accurate count
				await assetStore.refreshAdAccounts()
				const adAccountsCount = assetStore.adAccounts.length
				trackPostHogEvent(
					PostHogEvent.ONBOARDING_COMPLETE_AD_ACCOUNT_STEP,
					PostHogPath.ONBOARDING,
					{
						ad_accounts_connected: adAccountsCount,
						automatic_toggle: accountSelection.tracking ? 'on' : 'off'
					}
				)

				// Signal that ad account is connected (for sidebar step completion)
				onAdAccountConnected?.()

				setInternalStep('done')
				onSuccess?.()
			} else {
				setInternalStep('done')
				onSuccess?.()
			}
		} catch (err) {
			if (
				(err as any)?.response?.data?.code === 'AD_ACCOUNT_SPEND_LOW' ||
				(err as any)?.response?.data?.code === 'AD_ACCOUNT_FIT_CALL_REQUIRED'
			) {
				setScreeningTier(
					(err as any).response.data.code === 'AD_ACCOUNT_SPEND_LOW'
						? 'low'
						: 'screening'
				)
			} else if (isZeroSpendError(err)) {
				showNoSpend()
			} else {
				setError(parseError(err) || 'Failed to add account')
			}
		} finally {
			setLoading(false)
		}
	}

	const getAccountOptions = () => {
		if (!accountLink) return []

		if (platform === 'GADS') {
			return (accountLink as GoogleOAuthAccountLinkDTO).clients || []
		} else if (platform === 'FADS') {
			return (accountLink as FacebookAccountLinkDTO).clients || []
		} else if (platform === 'BADS') {
			return (accountLink as BingAccountLinkDTO).clients || []
		}
		return []
	}

	const accountOptions = getAccountOptions()
	const isWaiting = internalStep === 'waiting'
	const isAccountSelection = internalStep === 'account-selection'
	const isDone = internalStep === 'done'

	const getConnectedAccountInfo = () => {
		if (!accountLink) return null

		if (platform === 'GADS') {
			return (accountLink as GoogleOAuthAccountLinkDTO).name
		} else if (platform === 'FADS') {
			return (accountLink as FacebookAccountLinkDTO).email
		} else if (platform === 'BADS') {
			return (accountLink as BingAccountLinkDTO).email
		}
		return null
	}

	const handleAuthorizationChange = (link: AccountLinkType) => {
		setSelectedAccountLinkId(link.id)
		setAccountLink(link)
		setAccountSelection({
			selected: null,
			client: null,
			tracking: accountSelection.tracking,
			set_custom_audience: accountSelection.set_custom_audience,
			pixel_id: null
		})
		setMccLink(null)
		setPixelOptions([])
	}

	const fetchPixels = async (linkId: string, accountNumber: string) => {
		if (!account?.id || !asset?.id) return

		setLoadingPixels(true)
		try {
			const pixels = await Onboarding.getPixels(
				account.id,
				asset.id,
				linkId,
				accountNumber
			)
			setPixelOptions(pixels || [])
			// Auto-select first pixel if available and no pixel is currently selected
			if (pixels && pixels.length > 0) {
				setAccountSelection((prev) => ({
					...prev,
					pixel_id: prev.pixel_id || pixels[0].id
				}))
			}
		} catch (err) {
			console.error('Failed to fetch pixels:', err)
			setPixelOptions([])
		} finally {
			setLoadingPixels(false)
		}
	}

	useEffect(() => {
		setMccClientLoading(false)
		return () => {
			++mccSelectionRequest.current
		}
	}, [platform, accountLink?.id])

	const handleAccountChange = (selected: any) => {
		const request = ++mccSelectionRequest.current
		if (!selected?.manager) setMccClientLoading(false)
		// A new pick in the main list starts over: the client picked under a
		// manager account goes, and so does the manager link itself when the
		// new pick is a plain account, or the step stays locked on the MCC.
		if (!selected?.manager && mccLink) setMccLink(null)
		setAccountSelection({
			...accountSelection,
			selected,
			client: null,
			pixel_id: null // Reset pixel when account changes
		})

		if (selected?.manager && accountLink) {
			//run after a small delay to allow the account to be selected
			setMccClientLoading(true)
			const payload: any = {
				account_number: selected.account_number,
				link_id: accountLink.id,
				set_tracking_template:
					platform === 'GADS' || platform === 'BADS'
						? accountSelection.tracking
						: undefined,
				set_custom_audience:
					platform === 'FADS' || platform === 'BADS'
						? accountSelection.set_custom_audience
						: undefined,
				pixel_id: platform === 'FADS' ? accountSelection.pixel_id : undefined
			}

			// For Bing Ads, we need to send customer_id and ad_account_id
			if (platform === 'BADS' && accountSelection.selected) {
				const selectedAccount =
					accountSelection.selected as BingAdsAccountBasicInfoDTO
				const bingLink = accountLink as BingAccountLinkDTO
				payload.customer_id = bingLink.external_id || ''
				payload.ad_account_id = selectedAccount.id?.toString() || ''
			}

			Onboarding.addAdAccountToAsset(account.id, asset.id, payload)
				.then((result) => {
					if (
						request === mccSelectionRequest.current &&
						result.type === 'link' &&
						result.data
					) {
						setMccLink(result.data as MCCAccountLinkDTO)
						setAccountSelection({
							...accountSelection,
							selected: result.data as any
						})
					}
				})
				.catch((error) => {
					if (request === mccSelectionRequest.current)
						setError(parseError(error) || 'Could not load manager accounts')
				})
				.finally(() => {
					if (request === mccSelectionRequest.current)
						setMccClientLoading(false)
				})
		}

		// Fetch pixels for Facebook when account is selected
		if (platform === 'FADS' && accountLink && selected?.account_number) {
			fetchPixels(accountLink.id, selected.account_number)
		} else {
			setPixelOptions([])
		}
	}

	// Fetch pixels when Facebook account is selected
	useEffect(() => {
		if (
			platform === 'FADS' &&
			accountLink &&
			accountSelection.selected &&
			account?.id &&
			asset?.id
		) {
			const selectedAccount = accountSelection.selected as any
			if (selectedAccount?.account_number) {
				fetchPixels(accountLink.id, selectedAccount.account_number)
			}
		} else if (platform !== 'FADS') {
			setPixelOptions([])
		}
	}, [
		platform,
		accountLink?.id,
		accountSelection.selected,
		account?.id,
		asset?.id
	])

	const handlePixelChange = (pixelId: string | null) => {
		setAccountSelection({
			...accountSelection,
			pixel_id: pixelId
		})
	}

	const handleMccClientChange = (client: any) => {
		setAccountSelection({
			...accountSelection,
			client
		})
	}

	const handleToggleChange = (updates: Partial<AccountSelectionState>) => {
		setAccountSelection({
			...accountSelection,
			...updates
		})
	}

	const handleSelectDifferentAccount = () => {
		setShowZeroSpendModal(false)
		setError(null)
		// Return the user to the account selection screen to pick a spending
		// account, with the picker empty: the account they just saw screened out
		// must not sit there pre-selected, one Continue away from the same answer.
		setScreeningTier(null)
		setMccLink(null)
		setAccountSelection({
			...accountSelection,
			selected: null,
			client: null,
			pixel_id: null
		})
		setInternalStep('account-selection')
	}

	return (
		<div
			className="bg-white rounded-[24px] shadow-lg md:pt-[32px] pt-[24px] md:pb-[32px] pb-[24px] md:px-[48px] px-[24px]"
			style={{ width: '620px', maxWidth: '100%' }}
		>
			{(internalStep === 'idle' || internalStep === 'waiting') && (
				<HeaderSection asset={asset} isOnboarding={isOnboarding} />
			)}

			{internalStep === 'idle' && (
				<PlatformSelectionView
					onPlatformSelect={handlePlatformSelect}
					loading={loading || prefetching}
					isOnboarding={isOnboarding}
				/>
			)}

			{isWaiting && <WaitingView onCancel={handleCancelAuth} />}

			{isAccountSelection && platform && (
				<AccountSelectionView
					platform={platform}
					accountLink={accountLink}
					availableAccountLinks={availableAccountLinks}
					selectedAccountLinkId={selectedAccountLinkId}
					accountSelection={accountSelection}
					mccLink={mccLink}
					accountOptions={accountOptions}
					pixelOptions={pixelOptions}
					loading={loading || loadingPixels}
					error={error}
					onBack={() => {
						setInternalStep('idle')
						setPlatform(null)
						setAccountLink(null)
						setSelectedAccountLinkId(null)
						setMccLink(null)
						setError(null)
					}}
					onAuthorizationChange={handleAuthorizationChange}
					onAccountChange={handleAccountChange}
					onMccClientChange={handleMccClientChange}
					onToggleChange={handleToggleChange}
					onPixelChange={handlePixelChange}
					onCreateNewAuthorization={handleCreateNewAuthorization}
					onSubmit={handleAccountSubmit}
					getConnectedAccountInfo={getConnectedAccountInfo}
					isOnboarding={isOnboarding}
					mccClientLoading={mccClientLoading}
				/>
			)}

			{internalStep === 'tracking-confirmation' && platform && (
				<TrackingConfirmationView
					platform={platform}
					existingTT={accountSelection.existingTT || null}
					existingUrlSuffix={accountSelection.existingUrlSuffix || null}
					campaignsToUpdate={accountSelection.campaignsToUpdate || []}
					adAccountNumber={selectedAccountInfo?.account_number || ''}
					loading={loading}
					onBack={() => setInternalStep('account-selection')}
					error={error}
					onConfirm={handleTrackingConfirm}
				/>
			)}

			{/* Spend screening: a modal over the picker, like the zero-spend one,
			    so the user reads it as "something stopped me here". */}
			<SpendScreeningModal
				tier={screeningTier}
				onChooseAnotherAccount={handleSelectDifferentAccount}
				onManageSubscription={() =>
					navigate(`/settings/workspace/${account.id}/billing`)
				}
			/>

			{isDone && <DoneView />}

			<ZeroSpendModal
				open={showZeroSpendModal}
				onSelectDifferentAccount={handleSelectDifferentAccount}
			/>
		</div>
	)
}
